import React, { useEffect, useState } from 'react'
import tradegridPlusService from 'Services/tradegridPlusService'
import { transactionBXService } from 'BoostXpressAdmin/Services/transactionBXService'
import { tgplusModel } from 'Models'
import { TransactionXModel } from 'BoostXpressAdmin/Model/transactionXModel'
import { CurrencyFormatter } from 'Helpers'

interface SubscriptionOrdersTableProps {
  activeCategory: string
  resolvedSubTab: string
  pageNumber: number
  pageSize: number
  onTotalChange: (total: number) => void
}

const SubscriptionOrdersTable: React.FC<SubscriptionOrdersTableProps> = ({
  activeCategory,
  resolvedSubTab,
  pageNumber,
  pageSize,
  onTotalChange,
}) => {
  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      try {
        setIsLoading(true)
        if (resolvedSubTab === 'tgPlus') {
          const res = await tradegridPlusService.index(pageNumber, 'DESC', 'ALL')
          if (cancelled) return
          const responseData = res?.data.data
          setData(responseData?.dataset ?? [])
          onTotalChange(responseData?.total ?? 0)
        } else if (resolvedSubTab === 'transactions') {
          const res = await transactionBXService.index({
            page: pageNumber,
            limit: pageSize,
            sortOrder: 'DESC',
          })
          if (cancelled) return
          const responseData = res.data
          setData(responseData?.data ?? [])
          onTotalChange(responseData?.meta?.pagination?.total ?? 0)
        } else if (resolvedSubTab === 'orders') {
          // Placeholder for generic orders if any
          setData([])
          onTotalChange(0)
        }
      } catch {
        if (!cancelled) {
          setData([])
          onTotalChange(0)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchData()
    return () => {
      cancelled = true
    }
  }, [resolvedSubTab, pageNumber])

  return (
    <div className='bx-table-wrapper'>
      <table className='bx-table'>
        <thead>
          <tr>
            <th className='bx-finance__col-index'>#</th>
            <th className='bx-finance__col-user'>User</th>
            <th className='bx-finance__col-reference'>Phone</th>
            <th className='bx-finance__col-user'>Subscription / Type</th>
            <th className='bx-finance__col-amount'>Amount</th>
            <th className='bx-finance__col-status'>Status</th>
            <th className='bx-finance__col-date'>
              {resolvedSubTab === 'tgPlus' ? 'Expiry Date' : 'Date'}
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={7} className='bx-finance__empty'>
                Loading...
              </td>
            </tr>
          ) : data.length ? (
            data.map((item, index) => {
              const offset = (pageNumber - 1) * pageSize
              if (resolvedSubTab === 'tgPlus') {
                const sub = item as tgplusModel
                return (
                  <tr key={sub.id}>
                    <td className='bx-finance__col-index'>{offset + index + 1}</td>
                    <td className='bx-finance__col-user'>
                      {sub.customer ? `${sub.customer.firstName} ${sub.customer.lastName}` : '-'}
                    </td>
                    <td className='bx-finance__col-reference'>{sub.customer?.msisdn || '-'}</td>
                    <td className='bx-finance__col-user'>{sub.plan || '-'}</td>
                    <td className='bx-finance__col-amount'>
                      ₦{CurrencyFormatter(Number(sub.monthlySubscriptionMajor ?? 0))}
                    </td>
                    <td className='bx-finance__col-status'>
                      <span
                        className={`bx-status-pill bx-status-pill--${sub.status?.toLowerCase()}`}
                      >
                        {sub.status || 'Active'}
                      </span>
                    </td>
                    <td className='bx-finance__col-date'>
                      {sub.nextSubscriptionPaymentDueDate
                        ? new Date(sub.nextSubscriptionPaymentDueDate).toLocaleString()
                        : '-'}
                    </td>
                  </tr>
                )
              } else {
                const tx = item as TransactionXModel
                return (
                  <tr key={tx.resourceId}>
                    <td className='bx-finance__col-index'>{offset + index + 1}</td>
                    <td className='bx-finance__col-user'>
                      {tx.user ? `${tx.user.firstName} ${tx.user.lastName}` : '-'}
                    </td>
                    <td className='bx-finance__col-reference'>{tx.user?.phone || '-'}</td>
                    <td className='bx-finance__col-user'>{tx.transactionType || 'Transaction'}</td>
                    <td className='bx-finance__col-amount'>
                      ₦{CurrencyFormatter(Number(tx.amountMajor ?? 0))}
                    </td>
                    <td className='bx-finance__col-status'>
                      <span
                        className={`bx-status-pill bx-status-pill--${(tx.paymentStatus || 'pending').toLowerCase()}`}
                      >
                        {tx.paymentStatus || 'Pending'}
                      </span>
                    </td>
                    <td className='bx-finance__col-date'>
                      {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                )
              }
            })
          ) : (
            <tr>
              <td colSpan={7} className='bx-finance__empty'>
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default SubscriptionOrdersTable
